import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, X, Plus, Lock, Link2, Eye } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const PROPERTY_TYPES = [
  "Vacant Land", "Residential Infill Lot", "Teardown", "Oversized Residential Lot",
  "Subdividable Parcel", "Entitlement Opportunity", "Small Residential Development",
  "Existing Structure With Redevelopment Potential",
];
const STRATEGIES = [
  "Single-Family Spec Home", "Small-Lot Development", "Lot Split", "Subdivision",
  "Entitlement Flip", "SB 9", "SB 1123", "Duplex", "Multifamily", "ADU Opportunity",
  "Build and Sell", "Build and Hold",
];
const SITE_CHARS = [
  "Flat", "Gentle Slope", "Moderate Slope", "Hillside", "Corner Lot",
  "Multiple Street Frontages", "Paved Road", "Legal Access", "Lake View",
  "Mountain View", "Ocean View",
];
const EXCLUSIONS = [
  "Landlocked", "No Legal Access", "Extreme Hillside", "Floodway",
  "Major Environmental Constraints", "Manufactured / Mobile Home Only",
  "Commercial Only", "Industrial Only", "Excessive Retaining Wall Requirements",
  "Unusable Parcel Shape", "No Development Upside",
];
const WATER = ["Public Water Required", "Public Water Preferred", "Well Acceptable", "Unknown Acceptable"];
const SEWER = ["Public Sewer Required", "Public Sewer Preferred", "Septic Acceptable", "Unknown Acceptable"];
const ELECTRIC = ["At Property", "At Street", "Nearby", "Unknown Acceptable"];

const num = (v) => (v === "" || v == null ? null : Number(v));

function TagInput({ value = [], onChange, placeholder, testid }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setText("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input
          data-testid={testid}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
        />
        <Button type="button" variant="outline" size="icon" onClick={add}><Plus className="h-4 w-4" /></Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted">
              {v}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== v))}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ChipSelect({ options, value = [], onChange, allowCustom, testid }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  const custom = value.filter((v) => !options.includes(v));
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            data-testid={testid ? `${testid}-${o}` : undefined}
            onClick={() => toggle(o)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              value.includes(o) ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-input"
            }`}
          >
            {o}
          </button>
        ))}
        {custom.map((o) => (
          <button key={o} type="button" onClick={() => toggle(o)}
            className="text-xs px-3 py-1.5 rounded-full border bg-primary text-primary-foreground border-primary inline-flex items-center gap-1">
            {o} <X className="h-3 w-3" />
          </button>
        ))}
      </div>
      {allowCustom && (
        <div className="mt-2">
          <TagInput value={custom} onChange={(c) => onChange([...value.filter((v) => options.includes(v)), ...c])}
            placeholder="Add custom…" testid={testid ? `${testid}-custom` : undefined} />
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-sm">{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export default function BuyBoxEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", slug: "", description: "", public_headline: "", public_description: "", status: "draft",
    geography: { country: "USA", state: "", counties: [], cities: [], zips: [], radius: "" },
    property_types: [], development_strategies: [],
    criteria: {},
    zoning: { preferred: [], acceptable: [], avoid: [], residential_only: false, rezoning_considered: false, gpa_considered: false, notes: "" },
    utilities: { water: "", sewer: "", electric: "", gas: "", internet: "", notes: "" },
    site: { characteristics: [], max_slope: "", no_hoa_preferred: false },
    exclusions: [],
    financials: { public: false },
    contact: { name: "", email: "", phone: "", company: "" },
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/buybox/${id}`);
        setF((prev) => ({ ...prev, ...data, geography: { ...prev.geography, ...(data.geography || {}) },
          criteria: data.criteria || {}, zoning: { ...prev.zoning, ...(data.zoning || {}) },
          utilities: { ...prev.utilities, ...(data.utilities || {}) }, site: { ...prev.site, ...(data.site || {}) },
          financials: { ...prev.financials, ...(data.financials || {}) }, contact: { ...prev.contact, ...(data.contact || {}) } }));
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setNested = (group, k, v) => setF((p) => ({ ...p, [group]: { ...p[group], [k]: v } }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Buy Box name is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/buybox/${id}`, f);
        toast.success("Buy Box saved");
      } else {
        const { data } = await api.post("/buybox", f);
        toast.success("Buy Box created");
        nav(`/buybox/${data.id}/edit`, { replace: true });
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;
  }

  const NumField = ({ group, k, label, hint }) => (
    <Field label={label} hint={hint}>
      <Input type="number" data-testid={`bb-${group}-${k}`} value={f[group]?.[k] ?? ""}
        onChange={(e) => setNested(group, k, num(e.target.value))} />
    </Field>
  );

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto pb-24" data-testid="buy-box-editor">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => nav("/buybox")}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">{isEdit ? "Edit Buy Box" : "Create Buy Box"}</h1>
        </div>
        {isEdit && (
          <>
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/buybox/${f.slug}`); toast.success("Public link copied"); }}>
              <Link2 className="h-4 w-4 mr-1.5" /> Copy Link
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(`${window.location.origin}/buybox/${f.slug}`, "_blank")}>
              <Eye className="h-4 w-4 mr-1.5" /> Preview
            </Button>
          </>
        )}
        <Button data-testid="save-buy-box-btn" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={["identity", "geo", "types"]} className="space-y-3">
        <Section value="identity" title="1. Buy Box Identity">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Buy Box Name">
              <Input data-testid="bb-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Southern California Residential Land" />
            </Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="bb-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Public URL Slug" hint="Your public link: /buybox/<slug>">
              <Input data-testid="bb-slug" value={f.slug} onChange={(e) => set("slug", e.target.value)} placeholder="socal-residential-land" />
            </Field>
            <Field label="Internal Description">
              <Input data-testid="bb-description" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="For your team only" />
            </Field>
            <Field label="Public Headline">
              <Input data-testid="bb-public-headline" value={f.public_headline} onChange={(e) => set("public_headline", e.target.value)} placeholder="Arqitek Is Actively Buying Residential Land" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Public Description">
                <Textarea data-testid="bb-public-description" value={f.public_description} onChange={(e) => set("public_description", e.target.value)} rows={3} placeholder="Have an off-market property, vacant lot, teardown, or development opportunity? Send it to us for review." />
              </Field>
            </div>
          </div>
        </Section>

        <Section value="geo" title="2. Geography">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Country"><Input data-testid="bb-country" value={f.geography.country} onChange={(e) => setNested("geography", "country", e.target.value)} /></Field>
            <Field label="State"><Input data-testid="bb-state" value={f.geography.state} onChange={(e) => setNested("geography", "state", e.target.value)} placeholder="CA" /></Field>
            <Field label="Optional radius / target area"><Input value={f.geography.radius} onChange={(e) => setNested("geography", "radius", e.target.value)} placeholder="e.g. 30 miles from Menifee" /></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-3 mt-4">
            <Field label="Counties"><TagInput testid="bb-counties" value={f.geography.counties} onChange={(v) => setNested("geography", "counties", v)} placeholder="Add county" /></Field>
            <Field label="Cities"><TagInput testid="bb-cities" value={f.geography.cities} onChange={(v) => setNested("geography", "cities", v)} placeholder="Add city" /></Field>
            <Field label="ZIP Codes"><TagInput testid="bb-zips" value={f.geography.zips} onChange={(v) => setNested("geography", "zips", v)} placeholder="Add ZIP" /></Field>
          </div>
        </Section>

        <Section value="types" title="3. Property Types">
          <ChipSelect testid="bb-property-types" options={PROPERTY_TYPES} value={f.property_types} onChange={(v) => set("property_types", v)} allowCustom />
        </Section>

        <Section value="strat" title="4. Development Strategies">
          <ChipSelect testid="bb-strategies" options={STRATEGIES} value={f.development_strategies} onChange={(v) => set("development_strategies", v)} allowCustom />
        </Section>

        <Section value="criteria" title="5. Acquisition Criteria">
          <div className="grid gap-4 sm:grid-cols-3">
            <NumField group="criteria" k="min_price" label="Min Purchase Price" />
            <NumField group="criteria" k="max_price" label="Max Purchase Price" />
            <div />
            <NumField group="criteria" k="min_lot_sf" label="Min Lot SF" />
            <NumField group="criteria" k="max_lot_sf" label="Max Lot SF" />
            <div />
            <NumField group="criteria" k="min_acreage" label="Min Acreage" />
            <NumField group="criteria" k="max_acreage" label="Max Acreage" />
            <div />
            <NumField group="criteria" k="min_frontage" label="Min Frontage (ft)" />
            <NumField group="criteria" k="min_lot_width" label="Min Lot Width (ft)" />
            <NumField group="criteria" k="min_lot_depth" label="Min Lot Depth (ft)" />
            <NumField group="criteria" k="min_lots" label="Min Potential Lots" />
            <NumField group="criteria" k="max_lots" label="Max Potential Lots" />
          </div>
        </Section>

        <Section value="zoning" title="6. Zoning Criteria">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Preferred Zoning"><TagInput value={f.zoning.preferred} onChange={(v) => setNested("zoning", "preferred", v)} placeholder="R-1, R-2…" /></Field>
            <Field label="Acceptable Zoning"><TagInput value={f.zoning.acceptable} onChange={(v) => setNested("zoning", "acceptable", v)} placeholder="Add code" /></Field>
            <Field label="Zoning To Avoid"><TagInput value={f.zoning.avoid} onChange={(v) => setNested("zoning", "avoid", v)} placeholder="Add code" /></Field>
          </div>
          <div className="flex flex-wrap gap-6 mt-4">
            <SwitchRow label="Residential Only" checked={f.zoning.residential_only} onChange={(v) => setNested("zoning", "residential_only", v)} />
            <SwitchRow label="Rezoning Considered" checked={f.zoning.rezoning_considered} onChange={(v) => setNested("zoning", "rezoning_considered", v)} />
            <SwitchRow label="General Plan Amendment Considered" checked={f.zoning.gpa_considered} onChange={(v) => setNested("zoning", "gpa_considered", v)} />
          </div>
          <div className="mt-4"><Field label="Zoning Notes"><Textarea value={f.zoning.notes} onChange={(e) => setNested("zoning", "notes", e.target.value)} rows={2} /></Field></div>
        </Section>

        <Section value="util" title="7. Utilities">
          <div className="grid gap-4 sm:grid-cols-3">
            <UtilSelect label="Water" options={WATER} value={f.utilities.water} onChange={(v) => setNested("utilities", "water", v)} />
            <UtilSelect label="Sewer" options={SEWER} value={f.utilities.sewer} onChange={(v) => setNested("utilities", "sewer", v)} />
            <UtilSelect label="Electric" options={ELECTRIC} value={f.utilities.electric} onChange={(v) => setNested("utilities", "electric", v)} />
            <Field label="Gas"><Input value={f.utilities.gas} onChange={(e) => setNested("utilities", "gas", e.target.value)} /></Field>
            <Field label="Internet"><Input value={f.utilities.internet} onChange={(e) => setNested("utilities", "internet", e.target.value)} /></Field>
          </div>
          <div className="mt-4"><Field label="Utility Notes"><Textarea value={f.utilities.notes} onChange={(e) => setNested("utilities", "notes", e.target.value)} rows={2} /></Field></div>
        </Section>

        <Section value="site" title="8. Site Characteristics">
          <ChipSelect options={SITE_CHARS} value={f.site.characteristics} onChange={(v) => setNested("site", "characteristics", v)} allowCustom />
          <div className="grid gap-4 sm:grid-cols-2 mt-4">
            <Field label="Maximum Acceptable Slope (%)"><Input type="number" value={f.site.max_slope} onChange={(e) => setNested("site", "max_slope", e.target.value)} /></Field>
            <div className="flex items-end"><SwitchRow label="No HOA Preferred" checked={f.site.no_hoa_preferred} onChange={(v) => setNested("site", "no_hoa_preferred", v)} /></div>
          </div>
        </Section>

        <Section value="excl" title="9. Deal Breakers / Exclusions">
          <ChipSelect testid="bb-exclusions" options={EXCLUSIONS} value={f.exclusions} onChange={(v) => set("exclusions", v)} allowCustom />
        </Section>

        <Section value="fin" title="10. Financial Criteria (Internal Only)">
          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
            <Lock className="h-3.5 w-3.5" /> These values are for your underwriting and are hidden from the public page.
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <NumField group="financials" k="target_acq_price" label="Target Acquisition Price" />
            <NumField group="financials" k="max_offer" label="Maximum Allowable Offer" />
            <NumField group="financials" k="min_profit" label="Min Projected Profit" />
            <NumField group="financials" k="min_roi" label="Min ROI (%)" />
            <NumField group="financials" k="min_margin" label="Min Margin (%)" />
            <NumField group="financials" k="max_land_basis_unit" label="Max Land Basis / Unit" />
            <NumField group="financials" k="max_land_basis_lot" label="Max Land Basis / Lot" />
            <NumField group="financials" k="target_entitled_value" label="Target Entitled Value" />
            <NumField group="financials" k="max_site_dev_cost" label="Max Site Development Cost" />
            <NumField group="financials" k="max_construction_cost" label="Max Construction Cost" />
          </div>
        </Section>

        <Section value="contact" title="Public Contact Information">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Contact Name"><Input value={f.contact.name} onChange={(e) => setNested("contact", "name", e.target.value)} /></Field>
            <Field label="Company"><Input value={f.contact.company} onChange={(e) => setNested("contact", "company", e.target.value)} /></Field>
            <Field label="Email"><Input value={f.contact.email} onChange={(e) => setNested("contact", "email", e.target.value)} /></Field>
            <Field label="Phone"><Input value={f.contact.phone} onChange={(e) => setNested("contact", "phone", e.target.value)} /></Field>
          </div>
        </Section>
      </Accordion>
    </div>
  );
}

const Section = ({ value, title, children }) => (
  <AccordionItem value={value} className="border rounded-lg px-4 bg-card">
    <AccordionTrigger className="text-base font-medium hover:no-underline" data-testid={`bb-section-${value}`}>{title}</AccordionTrigger>
    <AccordionContent className="pt-2 pb-4">{children}</AccordionContent>
  </AccordionItem>
);

const SwitchRow = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <Switch checked={!!checked} onCheckedChange={onChange} />
    <span className="text-sm">{label}</span>
  </label>
);

const UtilSelect = ({ label, options, value, onChange }) => (
  <Field label={label}>
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Any / not specified</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </Field>
);
